import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Box, Typography, CircularProgress, Alert, useTheme } from '@mui/material';
import { API_BASE_URL } from '../../lib/config';

// Categorical series palette chosen to stay legible on both light and dark cards.
const COLORS = ['#4f8ef7', '#22c55e', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6', '#eab308', '#ef4444'];

interface PerformanceOverTimeProps {
  season: number;
}

interface UserWeekData {
  week: number;
  cumulative_wins: number;
  cumulative_total: number;
}

interface UserPerformance {
  user_id: string;
  display_name: string;
  weeks: UserWeekData[];
}

const PerformanceOverTime: React.FC<PerformanceOverTimeProps> = ({ season }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState<any[]>([]);
  const [users, setUsers] = useState<string[]>([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/analysis/performance-over-time/${season}`);

      if (!response.ok) {
        throw new Error('Failed to fetch performance data');
      }

      const result = await response.json();

      // Transform data for Recharts
      const transformed = transformData(result.users);
      setChartData(transformed.chartData);
      setUsers(transformed.userNames);
    } catch (err: any) {
      setError(err.message || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const transformData = (usersData: UserPerformance[]) => {
    // Get all unique weeks
    const allWeeks = new Set<number>();
    usersData.forEach(user => {
      user.weeks.forEach(week => allWeeks.add(week.week));
    });

    const weeks = Array.from(allWeeks).sort((a, b) => a - b);
    const userNames = usersData.map(u => u.display_name);

    // Transform to chart format
    const chartData = weeks.map(week => {
      const weekData: any = { week };
      usersData.forEach(user => {
        const weekInfo = user.weeks.find(w => w.week === week);
        weekData[user.display_name] = weekInfo?.cumulative_wins || 0;
      });
      return weekData;
    });

    return { chartData, userNames };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">No performance data available for this season yet.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Cumulative Wins Over Time
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Track how each player's total wins accumulate week by week throughout the season.
      </Typography>

      <ResponsiveContainer width="100%" height={450}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="week"
            stroke={theme.palette.text.secondary}
            tick={{ fill: theme.palette.text.secondary }}
            label={{ value: 'Week', position: 'insideBottom', offset: -5, fill: theme.palette.text.secondary }}
          />
          <YAxis
            stroke={theme.palette.text.secondary}
            tick={{ fill: theme.palette.text.secondary }}
            label={{ value: 'Cumulative Wins', angle: -90, position: 'insideLeft', fill: theme.palette.text.secondary }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 8,
              color: theme.palette.text.primary,
            }}
            labelStyle={{ color: theme.palette.text.primary }}
          />
          <Legend wrapperStyle={{ color: theme.palette.text.secondary }} />
          {users.map((userName, index) => (
            <Line
              key={userName}
              type="monotone"
              dataKey={userName}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={3}
              dot={{ r: 5 }}
              activeDot={{ r: 7 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default PerformanceOverTime;
